import { EndUser } from '@/types'
import { formatDate, formatRelativeTime } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Eye, CheckCircle, XCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface UsersTableProps {
  users: EndUser[]
  loading?: boolean
  showPagination?: boolean
}

export function UsersTable({ users, loading, showPagination: _showPagination = false }: UsersTableProps) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    )
  }

  if (users.length === 0) {
    return <div className="text-center py-8 text-gray-500">No users found</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Mobile Number
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Verified
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Onboarding
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user._id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{user.full_name || 'N/A'}</div>
                {user.nric && <div className="text-xs text-gray-500">NRIC: {user.nric}</div>}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{user.mobile_number}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{user.email || '-'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {user.is_face_verified ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    <span className="text-xs">Yes</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-400">
                    <XCircle className="h-4 w-4 mr-1" />
                    <span className="text-xs">No</span>
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {user.is_onboarding ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    <span className="text-xs">Complete</span>
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-600">
                    <XCircle className="h-4 w-4 mr-1" />
                    <span className="text-xs">Incomplete</span>
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {user.createdAt && (
                  <>
                    <div className="text-sm text-gray-900">{formatRelativeTime(user.createdAt)}</div>
                    <div className="text-xs text-gray-500">{formatDate(user.createdAt)}</div>
                  </>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Button variant="ghost" size="sm" onClick={() => navigate(`/users/${user._id}`)} className="text-gray-700 dark:text-gray-300">
                  <Eye className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
